import circle from '@disy/cadenza-icons/circle.svg';
import square from '@disy/cadenza-icons/square.svg';
import poly from '@disy/cadenza-icons/polygon.svg';
import line from '@disy/cadenza-icons/line.svg';
import point from '@disy/cadenza-icons/point.svg';

import { type Icon, icon, type IconOptions } from 'cadenza/utils/icon/icon';

import i18n from './geometry-type.properties';

/**
 * Enum for geometry types, extending OpenLayers' GeometryType
 *
 * @readonly
 * @enum {string}
 */
export const GeometryType = {
  LINE: 'Line', // a LineString with maximal 2 Points
  OVAL: 'Oval',
  RECTANGLE: 'Rectangle',
  // Default OL types copied from ol/geom/GeometryType (except GeometryCollection unused for now)
  POINT: 'Point',
  LINE_STRING: 'LineString',
  LINEAR_RING: 'LinearRing',
  POLYGON: 'Polygon',
  MULTI_POINT: 'MultiPoint',
  MULTI_LINE_STRING: 'MultiLineString',
  MULTI_POLYGON: 'MultiPolygon',
  CIRCLE: 'Circle'
} as const;

export type GeometryType = typeof GeometryType[keyof typeof GeometryType];

// incomplete, add more icons when needed
const ICON_BY_GEOMETRY_TYPE = {
  [GeometryType.CIRCLE]: circle,
  [GeometryType.RECTANGLE]: square,
  [GeometryType.POLYGON]: poly,
  [GeometryType.MULTI_POLYGON]: poly,
  [GeometryType.LINE_STRING]: line,
  [GeometryType.POINT]: point,
  // Add icons for all geometry types (even if not used yet) to please TS
  [GeometryType.OVAL]: circle,
  [GeometryType.LINEAR_RING]: poly,
  [GeometryType.MULTI_LINE_STRING]: line,
  [GeometryType.LINE]: line,
  [GeometryType.MULTI_POINT]: point
} as const;

export function isMultiGeometryType (geometryType: GeometryType) {
  return (geometryType === GeometryType.MULTI_POINT
    || geometryType === GeometryType.MULTI_LINE_STRING
    || geometryType === GeometryType.MULTI_POLYGON);
}

export function getBaseGeometryType (geometryType: GeometryType): GeometryType {
  return geometryType.replace('Multi', '') as GeometryType;
}

export function getPrintName (geometryType: GeometryType): string {
  return i18n(geometryType);
}

export function getIcon (geometryType: GeometryType, options?: string | IconOptions): Icon {
  return icon(ICON_BY_GEOMETRY_TYPE[geometryType], options);
}

export function isPolygonAssignable (geometryType: GeometryType): boolean {
  return geometryType === GeometryType.POLYGON
    || geometryType === GeometryType.LINEAR_RING
    || geometryType === GeometryType.OVAL
    || geometryType === GeometryType.RECTANGLE
    || geometryType === GeometryType.CIRCLE;
}
